import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, MousePointerClick, Eye, Megaphone } from "lucide-react";
import { Switch } from "./ui/switch";
import { http, API } from "../lib/api";
import { AdForm, PLACEMENT_LABELS } from "./AdForm";

const AdRow = ({ ad, onEdit, onChange }) => {
  const toggle = async (active) => {
    await http.put(`/admin/ads/${ad.id}`, { ...ad, active }).then(onChange).catch(() => toast.error("Falha ao atualizar"));
  };
  const del = async () => {
    if (!window.confirm(`Excluir o anúncio "${ad.name}"?`)) return;
    await http.delete(`/admin/ads/${ad.id}`).then(() => { toast.success("Anúncio excluído"); onChange(); }).catch(() => toast.error("Falha ao excluir"));
  };
  const ctr = ad.impressions ? ((ad.clicks / ad.impressions) * 100).toFixed(1) : "0.0";
  return (
    <div className="grid grid-cols-[64px_1fr] gap-4 border-b border-white/5 py-4 sm:grid-cols-[96px_1fr_auto]" data-testid={`ad-row-${ad.id}`}>
      <div className="flex aspect-video items-center justify-center overflow-hidden bg-white/5">
        {ad.has_image ? <img src={`${API}/ads/${ad.id}/image?v=${encodeURIComponent(ad.updated_at || "")}`} alt="" className="h-full w-full object-cover" /> : <Megaphone size={18} className="text-rose-400" />}
      </div>
      <div className="min-w-0">
        <p className="truncate text-white">{ad.name}</p>
        <p className="font-mono-d mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-400">
          <span className="text-rose-400">{PLACEMENT_LABELS[ad.placement]}</span>
          <span>{ad.mode === "image" ? "Banner imagem" : ad.mode === "code" ? "Código" : "Link pop-under"}</span>
          <span className="flex items-center gap-1"><Eye size={12} /> {ad.impressions}</span>
          {ad.placement !== "popunder" && <span className="flex items-center gap-1"><MousePointerClick size={12} /> {ad.clicks} · {ctr}%</span>}
        </p>
      </div>
      <div className="col-span-2 flex items-center gap-3 sm:col-span-1">
        <label className="flex items-center gap-2 text-xs text-slate-300">
          <Switch checked={ad.active} onCheckedChange={toggle} data-testid={`ad-active-${ad.id}`} /> {ad.active ? "Ativo" : "Pausado"}
        </label>
        <button onClick={() => onEdit(ad)} className="p-2 text-slate-300 hover:bg-white/5 hover:text-white" aria-label="Editar" data-testid={`ad-edit-${ad.id}`}><Pencil size={16} /></button>
        <button onClick={del} className="p-2 text-rose-400 hover:bg-rose-950" aria-label="Excluir" data-testid={`ad-delete-${ad.id}`}><Trash2 size={16} /></button>
      </div>
    </div>
  );
};

export const AdsManager = () => {
  const [ads, setAds] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    http.get("/admin/ads").then((r) => setAds(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div data-testid="ads-manager">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-serif-d text-3xl text-white">Propagandas do <span className="italic text-rose-500">Site</span></h2>
          <p className="mt-2 text-sm text-slate-300">Gerencie os anúncios, pop-unders e banners que serão exibidos no seu site.</p>
        </div>
        <button onClick={() => setEditing({})} className="flex items-center gap-2 bg-rose-600 px-5 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-white hover:bg-rose-500" data-testid="ad-new-button">
          <Plus size={15} /> Novo anúncio
        </button>
      </div>
      {ads.length === 0 ? (
        <p className="border border-dashed border-white/10 p-10 text-center text-slate-400" data-testid="ads-empty">Nenhum anúncio cadastrado. Clique em “Novo anúncio” para começar.</p>
      ) : (
        <div data-testid="ads-list">{ads.map((a) => <AdRow key={a.id} ad={a} onEdit={setEditing} onChange={load} />)}</div>
      )}
      {editing && <AdForm ad={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
};
